import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import Card from 'app/frontend/base/components/Card'
import { useTeam } from 'app/frontend/base/config/context/team'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { Fragment, useLayoutEffect, useMemo, useRef, useState } from 'react'
import EventOverviewUser from './EventOverviewUser'
import dayjs, { type Dayjs } from 'dayjs'
import RelativeTime from 'dayjs/plugin/relativeTime'
import { Link } from 'react-router-dom'
import Select from 'app/frontend/base/components/Select'
import EventAvatar from 'app/frontend/base/components/EventAvatar'
import { eventInstant } from 'app/frontend/base/helpers/eventInstant'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { difficultiesLabels } from 'app/frontend/types/custom/Difficulty'
import { gameVersionHas } from 'app/frontend/types/custom/GameVersion'
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Loader2Icon,
  LockKeyhole,
} from 'lucide-react'
import { hasAccessToContent } from 'app/frontend/base/components/ProtectedContent'
import {
  EventOverviewStatusByEventValue,
  type UserStatView,
} from '../utils/useUserStat'
import {
  EventOverviewSortBy,
  EventOverviewSortDirection,
  sortEventOverviewRows,
} from '../utils/sortRows'
import { useTranslation } from 'react-i18next'
import { participantColor } from '../utils/participantColor'
import { presentParticipantStatuses } from 'app/frontend/types/custom/EventParticipantStatus'
import { useStickyTop } from 'app/frontend/base/hooks/useStickyTop'
import { useBreakpoint } from 'app/frontend/base/hooks/useBreakpoint'
import NoMemberWarning from '../../TeamLoot/components/NoMemberWarning'
import { useTeamOverviewEvents } from './useTeamOverviewEvents'

dayjs.extend(RelativeTime)

const formatCompactRelativeTime = (date: Dayjs, now = dayjs()) => {
  const seconds = date.diff(now, 'second')
  const absoluteSeconds = Math.abs(seconds)
  const isPast = seconds < 0

  const units = [
    { limit: 60, unit: 's', seconds: 1 },
    { limit: 60 * 60, unit: 'm', seconds: 60 },
    { limit: 60 * 60 * 24, unit: 'h', seconds: 60 * 60 },
    { limit: 60 * 60 * 24 * 30, unit: 'd', seconds: 60 * 60 * 24 },
    { limit: 60 * 60 * 24 * 365, unit: 'm', seconds: 60 * 60 * 24 * 30 },
    { limit: Infinity, unit: 'y', seconds: 60 * 60 * 24 * 365 },
  ]

  const unit =
    units.find(({ limit }) => absoluteSeconds < limit) ??
    units[units.length - 1]
  const value = Math.max(1, Math.round(absoluteSeconds / unit.seconds))
  const label = `${value}${unit.unit}`

  return isPast ? `${label} ago` : `in ${label}`
}

const EventOverview = () => {
  const isMobile = useBreakpoint() === 'xs'
  const eventWidthPx = 75
  const primaryColumnWidthPx = isMobile ? 110 : 160
  const summaryColumnWidthPx = isMobile ? 0 : 75
  const fixedColumnCount = isMobile ? 1 : 2
  const { team, guild } = useTeam()
  const { hasAccess: isAdmin } = hasAccessToContent(
    'admin',
    'base',
    guild,
    team,
  )
  const { t } = useTranslation()
  const {
    data: { user, gameVersion },
  } = usePageInfo()
  const [sortBy, setSortBy] = useState<EventOverviewSortBy>('role')
  const [sortDirection, setSortDirection] =
    useState<EventOverviewSortDirection>('asc')
  const [statView, _setStatView] = useState<UserStatView>('presence')
  const cardRef = useRef<HTMLDivElement>(null)
  const [eventAreaWidthPx, setEventAreaWidthPx] = useState(0)

  const maxVisibleEvents = useMemo(() => {
    if (eventAreaWidthPx <= 0) return 0
    return Math.max(1, Math.floor(eventAreaWidthPx / eventWidthPx))
  }, [eventAreaWidthPx, eventWidthPx])
  const {
    events: overviewEvents,
    visibleEvents,
    isLoadingEvents,
    isFetchingEvents,
    showBackToNow,
    disableLeftButton,
    disableRightButton,
    showLeftLoader,
    showRightLoader,
    navigateLeft,
    navigateRight,
    backToNow,
  } = useTeamOverviewEvents(team.id, maxVisibleEvents)

  const sortOptions = useMemo(
    () => [
      { label: 'name', value: 'name' },
      { label: 'role', value: 'role' },
      { label: 'class', value: 'class' },
      { label: statView, value: 'custom' },
    ],
    [statView],
  )

  const stickyTop = useStickyTop(true)

  const baseRows = useMemo(
    () =>
      team.members?.filter(
        (member) =>
          member.teamRank.raid_visibility &&
          member.teamRank.player_type !== 'alt',
      ) ?? [],
    [team.members],
  )

  const statusByRowAndEvent = useMemo(() => {
    const rowByCharacterId = new Map<number, number>()
    const rowIndexesByUserId = new Map<number, number[]>()
    const baseRowCharacterIds = new Set<number>()
    baseRows.forEach((member, rowIndex) => {
      const characterId = member.characterReference.id
      const userId = member.characterReference.userId
      rowByCharacterId.set(characterId, rowIndex)
      baseRowCharacterIds.add(characterId)
      if (userId === null) return
      const rowIndexes = rowIndexesByUserId.get(userId) ?? []
      rowIndexes.push(rowIndex)
      rowIndexesByUserId.set(userId, rowIndexes)
    })

    const matrix = baseRows.map(
      () => new Map<number, EventOverviewStatusByEventValue>(),
    )

    visibleEvents.forEach((event) => {
      event.participantReferences.forEach((participant) => {
        if (participant.characterId === undefined) return
        const rowIndex = rowByCharacterId.get(participant.characterId)
        if (rowIndex === undefined) return
        matrix[rowIndex].set(event.id, {
          status: participant.status,
          priority: 0,
          participantId: participant.id,
        })
      })
      event.participantReferences.forEach((participant) => {
        if (participant.characterId === undefined) return
        if (baseRowCharacterIds.has(participant.characterId)) return
        const userId = participant.characterReference.userId
        if (userId === null) return
        const rowIndexes = rowIndexesByUserId.get(userId)
        if (!rowIndexes?.length) return
        rowIndexes.forEach((rowIndex) => {
          if (matrix[rowIndex].has(event.id)) return
          matrix[rowIndex].set(event.id, {
            status: participant.status,
            priority: 0,
            participantId: participant.id,
          })
        })
      })
    })

    return matrix
  }, [baseRows, visibleEvents])

  const rows = useMemo(
    () =>
      sortEventOverviewRows({
        rows: baseRows.map((member, rowIndex) => ({
          key: member.id,
          main: member,
          statusByEvent: statusByRowAndEvent[rowIndex],
        })),
        visibleEvents,
        statView,
        sortBy,
        sortDirection,
      }),
    [
      baseRows,
      sortBy,
      sortDirection,
      statView,
      statusByRowAndEvent,
      visibleEvents,
    ],
  )

  const orderedRows = useMemo(() => {
    if (!user) return rows
    const ownRows = rows.filter(
      (row) => row.main.characterReference.userId === user.id,
    )
    const otherRows = rows.filter(
      (row) => row.main.characterReference.userId !== user.id,
    )
    return [...ownRows, ...otherRows]
  }, [rows, user])

  const ownRowsCount = useMemo(
    () =>
      orderedRows.filter(
        (row) => row.main.characterReference.userId === user?.id,
      ).length,
    [orderedRows, user],
  )
  const minEventColumns = maxVisibleEvents
  const placeholderColumnCount = Math.max(
    0,
    minEventColumns - visibleEvents.length,
  )
  const renderEventColumnCount = visibleEvents.length + placeholderColumnCount
  const tableWidthPx =
    primaryColumnWidthPx +
    summaryColumnWidthPx +
    eventWidthPx * renderEventColumnCount

  useLayoutEffect(() => {
    const cardElement = cardRef.current
    if (!cardElement) return
    const updateEventAreaWidth = () => {
      const tableWidth = cardElement.getBoundingClientRect().width
      const nextEventAreaWidth = Math.max(
        0,
        tableWidth - primaryColumnWidthPx - summaryColumnWidthPx,
      )
      setEventAreaWidthPx(Math.round(nextEventAreaWidth))
    }

    updateEventAreaWidth()
    const resizeObserver = new ResizeObserver(updateEventAreaWidth)
    resizeObserver.observe(cardElement)
    return () => {
      resizeObserver.disconnect()
    }
  }, [primaryColumnWidthPx, summaryColumnWidthPx])

  return (
    <div>
      <div className='grid grid-cols-3 items-center pb-1'>
        <Button
          type='button'
          variant='outline'
          size='xs'
          onClick={navigateLeft}
          disabled={disableLeftButton}
          className={cn('justify-self-start h-7')}
        >
          {showLeftLoader ? (
            <Loader2Icon className='h-4 w-4 animate-spin' />
          ) : (
            <ChevronLeftIcon className='h-4 w-4' />
          )}
        </Button>

        {showBackToNow ? (
          <Button
            type='button'
            variant='outline'
            size='xs'
            onClick={backToNow}
            disabled={isFetchingEvents}
            className='justify-self-center'
          >
            Back to now
          </Button>
        ) : (
          <div />
        )}

        <Button
          type='button'
          variant='outline'
          size='xs'
          onClick={navigateRight}
          disabled={disableRightButton}
          className='justify-self-end h-7'
        >
          {showRightLoader ? (
            <Loader2Icon className='h-4 w-4 animate-spin' />
          ) : (
            <ChevronRightIcon className='h-4 w-4' />
          )}
        </Button>
      </div>

      <Card ref={cardRef} className='pt-0 flex'>
        {!overviewEvents.length && !isLoadingEvents ? (
          <div className='flex justify-center items-center mt-4'>
            <p className='text-muted-foreground'>
              No upcoming events found.
              {isAdmin &&
                ' Set up a schedule or create an event to get started.'}
            </p>
          </div>
        ) : (
          <table
            className={cn(
              'table-fixed border-separate border-spacing-y-1 relative transition-opacity',
              visibleEvents.length === 0 && 'opacity-0 pointer-events-none',
            )}
            style={{ width: tableWidthPx, maxWidth: tableWidthPx }}
          >
            <colgroup>
              <col
                style={{
                  width: primaryColumnWidthPx,
                  maxWidth: primaryColumnWidthPx,
                }}
              />
              {!isMobile && (
                <col
                  style={{
                    width: summaryColumnWidthPx,
                    maxWidth: summaryColumnWidthPx,
                  }}
                />
              )}
              {visibleEvents.map((event) => (
                <col
                  key={event.id}
                  style={{ width: eventWidthPx, maxWidth: eventWidthPx }}
                />
              ))}
              {Array.from({ length: placeholderColumnCount }).map(
                (_, index) => (
                  <col
                    key={`placeholder-col-${index}`}
                    style={{ width: eventWidthPx, maxWidth: eventWidthPx }}
                  />
                ),
              )}
            </colgroup>
            <thead className='[&_th]:align-bottom'>
              <tr>
                <th
                  className={cn(
                    'sticky z-30 bg-card border-b font-normal text-xs',
                    stickyTop,
                  )}
                >
                  <div className='flex mb-1 gap-2 items-center'>
                    <div>
                      {sortDirection === 'asc' ? (
                        <ArrowUpIcon
                          className='w-4 h-4 cursor-pointer'
                          onClick={() => setSortDirection('desc')}
                        />
                      ) : (
                        <ArrowDownIcon
                          className='w-4 h-4 cursor-pointer'
                          onClick={() => setSortDirection('asc')}
                        />
                      )}
                    </div>

                    <div className='flex items-center gap-1'>
                      <span className='text-muted-foreground'>sort by</span>
                      <Select
                        value={sortBy}
                        setValue={(value) => {
                          if (!value) return
                          setSortBy(
                            value as 'name' | 'role' | 'class' | 'custom',
                          )
                        }}
                        options={sortOptions}
                        hideIcon
                        className='bg-transparent hover:bg-transparent border-0 w-fit font-normal text-xs p-0 h-5'
                        placeholder='Sort by'
                        optionWidth='fit'
                      />
                    </div>
                  </div>
                </th>
                {!isMobile && (
                  <th
                    className={cn('sticky z-30 bg-card border-b', stickyTop)}
                  />
                )}

                {visibleEvents.map((event) => {
                  const eventDate = dayjs(event.date)
                  const eventStartsAt = eventInstant(
                    event.startsAt,
                    event.timezone,
                  )
                  const now = dayjs()
                  const shouldShowYear =
                    (eventDate.isAfter(now.add(6, 'month')) ||
                      eventDate.isBefore(now.subtract(6, 'month'))) &&
                    eventDate.year() !== now.year()
                  const shouldShowRelativeTime =
                    eventStartsAt.diff(now, 'hour', true) <= 24

                  return (
                    <th
                      key={event.id}
                      className={cn('sticky z-30 bg-card border-b', stickyTop)}
                    >
                      <Tooltip delayDuration={300} disableHoverableContent>
                        <TooltipTrigger asChild>
                          <Link
                            to={`${team.path}/events/${event.id}`}
                            state={{
                              eventsReturn:
                                window.location.search + window.location.hash,
                            }}
                            className='flex flex-col items-center px-1 pb-1 pt-2 group'
                          >
                            <span
                              className={cn(
                                'font-medium whitespace-nowrap leading-tight transition-colors',
                                event.status === 'cancelled'
                                  ? 'group-hover:text-destructive-foreground'
                                  : 'group-hover:text-primary',
                              )}
                            >
                              {eventDate.format('MMM D')}
                            </span>

                            <span className='text-xs text-muted-foreground whitespace-nowrap font-normal'>
                              {shouldShowYear
                                ? eventDate.format('YYYY')
                                : shouldShowRelativeTime
                                ? formatCompactRelativeTime(eventStartsAt, now)
                                : eventDate.format('ddd')}
                            </span>

                            <div className='relative'>
                              <EventAvatar
                                eventReference={event}
                                variant='square'
                                className={cn(
                                  'rounded-[8px] border h-9 w-16 min-w-16 my-1 group-hover:border-primary transition-colors',
                                  event.status === 'cancelled' &&
                                    'grayscale opacity-80',
                                )}
                              />

                              {event.optional ? (
                                <span className='absolute top-[6px] right-[4px] bg-info border-transparent text-info-foreground flex items-center justify-center px-1 py-0.5 h-4 border rounded-[6px] text-xs font-normal'>
                                  Optional
                                </span>
                              ) : event.status === 'locked' ? (
                                <span className='absolute top-[6px] right-[1.5px] bg-warning border-transparent text-warning-foreground flex items-center justify-center pl-0.5 pr-0.5 py-0.5 h-4 border rounded-[6px] text-xs font-normal'>
                                  <LockKeyhole size={12} className='mr-0.5' />{' '}
                                  Locked
                                </span>
                              ) : null}

                              <span
                                className={cn(
                                  'absolute right-[6px] bottom-[5px] capitalize flex items-center justify-center px-1 py-0.5 bg-card-nested h-4 border rounded-t-[6px] text-xs font-normal border-b-transparent transition-colors max-w-[56px]',
                                )}
                              >
                                <span className='truncate'>
                                  {event.activityType === 'raids'
                                    ? event.primaryDifficulty?.[0]
                                    : event.activityType === 'custom'
                                    ? event.title
                                    : t(
                                        `dashboard.team.events.activity-types.${event.activityType}`,
                                      )}
                                </span>
                              </span>
                            </div>

                            {event.status === 'cancelled' ? (
                              <span className='font-normal text-sm text-destructive-foreground'>
                                cancelled
                              </span>
                            ) : (
                              <span className='flex items-center gap-1 font-normal text-muted-foreground text-xs'>
                                <span
                                  className={cn(
                                    'font-semibold text-sm',
                                    participantColor(event, gameVersion),
                                  )}
                                >
                                  {
                                    event.participantReferences.filter((p) =>
                                      presentParticipantStatuses.includes(
                                        p.status,
                                      ),
                                    ).length
                                  }
                                </span>
                                <span>/</span>
                                <span>
                                  {event.participantReferences.length}
                                </span>
                              </span>
                            )}
                          </Link>
                        </TooltipTrigger>
                        <TooltipContent
                          sideOffset={-6}
                          className='flex flex-col items-center'
                        >
                          <div>
                            {`${event.title}${
                              event.activityType === 'raids' &&
                              event.primaryDifficulty &&
                              gameVersionHas('difficulties')
                                ? ` (${
                                    difficultiesLabels[event.primaryDifficulty]
                                  })`
                                : ''
                            }`}
                          </div>

                          <span className='text-xs text-muted-foreground'>
                            {event.startTime} - {event.finishTime}
                          </span>

                          <span className='text-xs text-foreground'>
                            {eventStartsAt.fromNow()}
                          </span>
                        </TooltipContent>
                      </Tooltip>
                    </th>
                  )
                })}
              </tr>
            </thead>

            <tbody>
              {orderedRows.map((row, index) => (
                <Fragment key={row.key}>
                  {index === ownRowsCount &&
                  ownRowsCount > 0 &&
                  ownRowsCount < orderedRows.length ? (
                    <tr key='own-rows-divider'>
                      <td
                        colSpan={renderEventColumnCount + fixedColumnCount}
                        className='h-2 p-0'
                      />
                    </tr>
                  ) : null}
                  <EventOverviewUser
                    key={row.key}
                    main={row.main}
                    visibleEvents={visibleEvents}
                    statusByEvent={row.statusByEvent}
                    statView={statView}
                    hideStats={isMobile}
                    iconType={isMobile ? 'role' : 'both'}
                  />
                </Fragment>
              ))}

              {orderedRows.length === 0 ? (
                <tr>
                  <td colSpan={renderEventColumnCount + fixedColumnCount}>
                    <NoMemberWarning section='events' />
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  )
}

export default EventOverview
